import React, { useState, useEffect, useRef } from 'react';
import { Page, Dashboard, ReflexHorizontalContainer } from '../../templates';
import { Content } from '../../organisms';
import  CadDataFilter from './Filter';
import { ImgUploadTab } from './ImgUploadTab';
import { PrdtDescriptionTab } from './PrdtDescriptionTab';
import { ChangeCommentTab } from './ChangeCommentTab';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { FlexBoxVertical, ToggleSwitch, FlexBoxHorizontal, BackdropPanel, Checkbox, Typography } from '../../atoms';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Menu from '../../../utils/menu';
import { LanguageButton } from '../../molecules';
import i18n from '../../../utils/i18n';
import { useTranslation } from 'react-i18next';
import { translateText } from '../../../utils/translateText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinearProgress from '@mui/material/LinearProgress';
import palette from '../../../utils/colorPalette';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { styled } from 'styled-components';
import { useUser } from '../../../contexts/UserContext';
import Unauthorized from '../../auth/Unauthorized';
import { useLocation } from 'react-router-dom';

function CadDataDX({ onSubTabChange = null }) {
  const { t } = useTranslation();
  const language = i18n.language;
  const location = useLocation();
  const ismatrixBoardPage = location.pathname === '/interstellar/matrix-board';
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [lifeStyleList, setLifeStyleList] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [viewMode, setViewMode] = useState('List');
  const [translatedColumns, setTranslatedColumns] = useState(columns(language));
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [imgForDownload, setImgForDownload] = useState(new Set());
  const [isDownloading, setIsDownloading] = useState(false); // 다운로드 상태 관리
  const [abortController, setAbortController] = useState(null); // AbortController 상태 추가
  const [cnt, setCnt] = useState(0);
  const [rawCnt, setRawCnt] = useState(0);
  const [noImageOnly, setNoImageOnly] = useState(false);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const checkTimestampRef = useRef(null);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 탭 상태 추가
  const gridContainerRef = useRef(null);
  const currentSesnRef = useRef([]);
  const { user, permissions, hasPermission, loading: userLoading } = useUser();

  useEffect(() => {
    setIsLoading(true);
    // 페이지 로드 시 Snowflake 데이터 가져오기
    fetch('/api/cad-data', {
      method: 'POST',  // POST 메소드로 변경
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ brd_cd: 'X' })  
    })
      .then(response => response.json())
      .then(data => {
        const distinctData = Array.from(
          new Map(
            data.map(row => {
              const dropped = Object.fromEntries(Object.entries(row).filter(([k]) => k.toUpperCase() !== 'COLOR_CD'));
              return [JSON.stringify(dropped), dropped];
            })
          ).values()
        );
        console.log(distinctData);
        setSnowflakeData(distinctData);
        setFilterOptions({
          designer: createOptions(distinctData, 'dsgnr_nm'),
          item: createOptions(distinctData, 'item'),
          class1: createOptions(distinctData, 'parent_prdt_kind_nm'),
          class2: createOptions(distinctData, 'prdt_kind_nm'),
          sesn: createOptions(distinctData, 'sesn'),
          sex: createOptions(distinctData, 'sex'),
          fabInfo: createOptions(distinctData, 'fab_info'),
          lifeStyle: createOptions(distinctData, 'lifestyle_nm'),
        });
      })
      .catch(error => {
        console.error('Error fetching Snowflake data:', error);
      });
      setSelectedRow(null);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const checkImages = async () => {
      // noImageOnly가 false면 이미지 체크 없이 바로 데이터 설정
      if (!noImageOnly) {
        setFilteredTableData(tableData);
        setIsLoading(false);
        return;
      }

      // noImageOnly가 true일 때만 이미지 체크 수행
      const itemsWithLoadStatus = await Promise.all(
        tableData.map((item) =>
          new Promise((resolve) => {
            const img = new Image();
            
            img.onload = () => {
              resolve({ ...item, isLoaded: true });
            };
            
            img.onerror = () => {
              resolve({ ...item, isLoaded: false });
            };

            img.src = item.img;
          })
        )
      );
      
      setFilteredTableData(itemsWithLoadStatus.filter(item => !item.isLoaded));
      setIsLoading(false);
    };
    
    checkImages();
  }, [noImageOnly, tableData]);


  const filterData = (options) => {
    currentSesnRef.current = options.sesn?.map(s => s.value) || [];
    const hasSearchValue = options.searchValue && options.searchValue.trim() !== '';
  
    // 비교 키 정의
    const comparisonKeys = {
      class1: 'parent_prdt_kind_nm',
      class2: 'prdt_kind_nm',
      item: 'item',
      sesn: 'sesn',
      designer: 'dsgnr_nm',
      sex: 'sex',
      fabInfo: 'fab_info',
    };
  
    // 필터링 로직
    const filteredResult = snowflakeData.filter((item) => {
      const searchValueMatch = hasSearchValue
        ? ['part_cd', 'color_cd', 'prdt_nm', 'prdt_nm_eng'].some((field) =>
            item[field]?.toString().toLowerCase().includes(options.searchValue.toLowerCase())
          )
        : true;

        const comparisonMatch = Object.keys(comparisonKeys).every((key) => {
          if (options[key].length === filterOptions[key].length) return true;
          return options[key].some(
            (option) => option.value.toUpperCase() === String(item[comparisonKeys[key]]).toUpperCase()
          );
        });

      return searchValueMatch && comparisonMatch;
    });

    setFilteredData(filteredResult);
  };

  // lifeStyleList가 변경되었을 때 rowSelectionModel을 설정
  useEffect(() => {
    if (lifeStyleList.length > 0) {
      setRowSelectionModel(lifeStyleList.map(item => item.lifeStyle));
    }
  }, [lifeStyleList]);

  // filteredData가 변경되면 lifeStyleList 계산
  useEffect(() => {
    if (filteredData.length > 0) {
      const lifeStyleCounts = filteredData.reduce((acc, data) => {
        const lifeStyle = data['lifestyle_nm'];
        acc[lifeStyle] = (acc[lifeStyle] || 0) + 1;
        return acc;
      }, {});

      const uniqueLifeStyles = Array.from(new Set(filteredData.map((data) => data['lifestyle_nm'])))
        .map((lifeStyle) => ({
          lifeStyle: `${lifeStyle} (${lifeStyleCounts[lifeStyle]})`,
        }));

      setLifeStyleList(uniqueLifeStyles);
    } else {
      setLifeStyleList([]);
    }
  }, [filteredData]);

  // rowSelectionModel 변경 시 tableData 필터링
  useEffect(() => {
    const lifeStyleOnly = rowSelectionModel.map((lifeStyle) => {
      const cleanedLifeStyle = lifeStyle.replace(/\s*\(\d+\)$/, '');
      return cleanedLifeStyle;
    });

    const newTableData = filteredData.filter((data) =>
      lifeStyleOnly.includes(data['lifestyle_nm'])
    );

    setTableData(newTableData);
  }, [rowSelectionModel, filteredData]);

  const handleImageSrcUpdate = (newImgSrc) => {
    const update = (prev) =>
      prev.map((row) =>
        row.part_cd === selectedRow.part_cd
          ? { ...row, img: newImgSrc }
          : row
      );
    setTableData(update);
    setFilteredTableData(update);
  };
    useEffect(() => {
      setIsImageLoading(true);
      const checkImages = async () => {
        const newValidImages = new Set();
        const currentTimestamp = Date.now();
        checkTimestampRef.current = currentTimestamp;
    
        await Promise.all(
          filteredTableData.map((item) =>
            new Promise((resolve) => {
              const img = new Image();
              img.src = item.img;
    
              img.onload = () => {
                newValidImages.add(item);
                resolve();
              };
    
              img.onerror = () => {
                resolve();
              };
            })
          )
        );
    
        // 마지막으로 호출된 작업의 결과만 반영
        if (checkTimestampRef.current === currentTimestamp) {
          setImgForDownload(newValidImages);
        }
        setIsImageLoading(false);
      };
    
      checkImages();
    }, [filteredTableData]); 
    
    const imageDownload = async () => {
      const imagesData = Array.from(imgForDownload);
      
      if (imagesData.length === 0) {
        alert('다운로드할 이미지가 없습니다.');
        return;
      }

      const controller = new AbortController(); // 새로운 AbortController 생성
      setAbortController(controller); // 상태에 저장

      try {
        setDownloadProgress(true);
        setIsDownloading(true); // 다운로드 시작
        const response = await fetch('/api/download-images', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            images: imagesData.map(item => ({
              url: item.img.split('?')[0],
              fileName: `${item.part_cd}.png`
            }))
          }),
          signal: controller.signal // 요청에 signal 추가
        });

        if (!response.ok) {
          throw new Error('이미지 다운로드 실패');
        }

        const blob = await response.blob();
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = 'cad_images.zip';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(downloadLink.href);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('다운로드가 취소되었습니다.'); // 다운로드 취소 로그
        } else {
          console.error('이미지 다운로드 중 오류 발생:', error);
          alert('이미지 다운로드 중 오류가 발생했습니다.');
        }
      } finally {
        setDownloadProgress(false);
        setIsDownloading(false); // 다운로드 종료
        setAbortController(null); // AbortController 상태 초기화
      }
    };

    useEffect(() => {
      setTranslatedColumns(columns(language).map((column) => {
        return {
          ...column, 
          headerName: translateText({
            text: column.headerName,
            globalLanguage: language,
          }),
        };
      }));
    }, [language]);

    useEffect(() => {
      setCnt(filteredTableData.length);
    }, [filteredTableData]);

  const downloadSampleExcel = async () => {
    const sesn = currentSesnRef.current;
    if (!sesn || sesn.length === 0) {
      alert('시즌을 선택해주세요.');
      return;
    }

    try {
      const response = await fetch('/api/cad-data/download-sample-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brd_cd: 'X', sesn, language }),
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Style_Initial_Plan_${sesn.join('_')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Excel download error:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    }
  };

  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      { hasPermission('cadDashboard-data', 'edit') ? (
        ismatrixBoardPage && onSubTabChange ? (
          <div style={{ position: 'fixed', top: '80px', left: '15px', zIndex: 1000, display: 'flex', gap: '4px' }}>
            <button style={{ fontSize: '12px', padding: '4px 8px', background: '#353339', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} disabled>Style Initial Plan</button>
            <button style={{ fontSize: '12px', padding: '4px 8px', background: '#fff', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }} onClick={() => onSubTabChange('upload')}>Data Upload</button>
          </div>
        ) : (
          <Menu
            targetPaths={['/interstellar/X/caddata', '/interstellar/X/caddata/upload']}
            menuItems={[
              { path: '/interstellar/X/caddata', label: 'Style Initial Plan' },
              { path: '/interstellar/X/caddata/upload', label: 'Data Upload' }
            ]}
            position={{ top: '10px', left: '-220px' }}
            buttonStyle={{ top: '20px' }}
          />
        )
      ) : null}

      <div style={{
        width: '70px',
        position: ismatrixBoardPage ? 'fixed' : 'absolute',
        top: ismatrixBoardPage ? '80px' : '9px',
        right: '170px',
        zIndex: 1000,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
      }}
        onClick={() => {
          window.open('https://fnf-digital.notion.site/Style-Initial-Plan-26afd8bcf5588093bb7af7181310294e?source=copy_link', '_blank');
        }}>
        <span style={{ marginRight: '2px' }}>ℹ️</span>
        <span style={{ fontSize: '12px', color: 'rgb(136, 136, 136)', fontWeight: '500' }}>Guide</span>
      </div>
      <LanguageButton position={{ top: ismatrixBoardPage ? '80px' : '10px', right: '10px', position: ismatrixBoardPage ? 'fixed' : 'absolute' }} />
      { !hasPermission('cadDashboard', 'view') ? <Unauthorized title="Style Initial Plan" contact="AX팀 국준호 과장" language={language} /> : 
      <Dashboard>
        <h1 style={{color:'#353339', margin: '30px'}}>Style Initial Plan</h1> 
        {filterOptions ? <CadDataFilter data={filterOptions} onSearch={filterData} downloadExcel={downloadSampleExcel} /> : <></>}
        <div style={{ alignSelf: 'flex-end', margin: '0 30px', display: 'flex', alignItems: 'center', gap: '10px'}}>
        <div
            style={{
              width: '130px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginRight: '10px',
            }}
            onClick={() => {
              setNoImageOnly(!noImageOnly)
            }}
          >
            <Checkbox
              checked={noImageOnly}
              setChecked={(value) => setNoImageOnly(value)}
            />
            <Typography
              variant="content2"
              style={{ cursor: 'pointer', marginLeft: '3px' }}
            >
              {translateText({text: '대표 이미지 미등록', globalLanguage: language})}
            </Typography>
          </div>
          <div style={{ 
            display: 'flex', 
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}>
            <Tooltip 
              title={
                <div style={{ whiteSpace: 'pre-line' }}>
                    {`${translateText({
                      text: "이미지 일괄 다운로드 (zip)\n이미지 개수와 네트워크 환경에 따라 다운로드 시간이 달라질 수 있으며,\n많을 경우 1분 이상 걸릴 수 있습니다.\n\n현재 조회된 이미지 수: ",
                      globalLanguage: language,
                    })} ${imgForDownload.size}`}
                </div>
              }
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    maxWidth: 500, // Tooltip의 최대 넓이를 500px로 설정
                  },
                },
              }}
            >
              <div style={{ position: 'relative', width: '145px' }}>
                <IconButton
                  onClick={() => {
                    if (isDownloading) {
                      // 다운로드 중일 때 클릭 시 다운로드 중단
                      abortController.abort(); // 다운로드 요청 취소
                      setDownloadProgress(false);
                      setIsDownloading(false);
                    } else {
                      imageDownload();
                    }
                  }}
                  sx={{
                    padding: '4px',
                    width: '100%',
                    height: '27px',
                    borderRadius: '0',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    transition: 'background-color 0.1s ease',
                    fontSize: '12px',
                  }}
                >
                  <SystemUpdateAltIcon sx={{ fontSize: 16, color: '#666', marginRight: '5px' }} />
                  {isDownloading ? (
                    <>
                      <span style={{  margin: '0 5px 0 0' }}>{translateText({text: '다운로드 중...', globalLanguage: language})}</span> 
                      <span style={{ color: 'rgb(255, 109, 135)', fontWeight: 'bold' }}>X</span>
                    </>
                  ) : (
                    isImageLoading ? translateText({text: '이미지 로드중...', globalLanguage: language}) : translateText({text: '대표 이미지 다운로드', globalLanguage: language})
                  )}
                </IconButton>
                {downloadProgress && (
                  <LinearProgress
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '2px',
                      backgroundColor: 'rgba(97, 97, 230, 0.2)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: palette.button.primary
                      }
                    }}
                  />
                )}
              </div>
            </Tooltip>
          </div>
          <ToggleSwitch onToggle={setViewMode}/>
        </div>
        <Content
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ 
            position: 'relative', 
            height: '100%',
            display: 'flex',
            width: '100%'
          }}>
            {/* 라이프스타일 선택 영역 */}
            <div style={{
              width: '220px',
              minWidth: '220px',
              height: '100%',
              borderRight: '1px solid #ddd',
              overflow: 'hidden',
            }}>
              <DataGridPremium
                sx={style}
                columns={[language === 'ko' ? { field: 'lifeStyle', headerName: '라이프스타일', width: 200 } : { field: 'lifeStyle', headerName: 'LifeStyle', width: 200 }]}
                rows={lifeStyleList}
                getRowId={(row) => row['lifeStyle']}
                hideFooter
                checkboxSelection
                rowHeight={40}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setRowSelectionModel(newRowSelectionModel);
                }}
                rowSelectionModel={rowSelectionModel}
              />
            </div>

            {/* 메인 데이터 영역 */}
            <div
              ref={gridContainerRef}
              style={{
                flex: 1,
                height: '100%',
                overflow: viewMode === 'List' ? 'hidden' : 'auto',
                transition: 'flex 0.3s ease'
              }}
            >
              {viewMode === 'List' ?
                  <DataGridPremium
                    sx={style}
                    columns={translatedColumns}
                    rows={filteredTableData}
                    getRowId={(row) => `${row['sesn']}_${row['part_cd']}`}
                    onRowClick={(params) => setSelectedRow(params.row)}
                    hideFooter
                    rowHeight={40}
                  /> : 
                  <Grid 
                    tableData={filteredTableData} 
                    setSelectedRow={setSelectedRow} 
                    language={language}
                    scrollContainerRef={gridContainerRef}
                  />
                }
            </div>
            
            {/* 사이드 패널 + 토글 래퍼 */}
            <div style={{
              width: isRightPanelCollapsed ? '0px' : '30%',
              minWidth: isRightPanelCollapsed ? '0px' : '350px',
              height: '100%',
              position: 'relative',
              display: 'flex',
              flexShrink: 0,
              transition: 'width 0.3s ease, min-width 0.3s ease',
              overflow: 'visible',
            }}>
              {/* 토글 버튼 */}
              <div
                style={{
                  position: 'absolute',
                  left: -30,
                  top: '20%',
                  transform: 'translateY(-50%)',
                  zIndex: 500,
                  backgroundColor: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '4px 0 0 4px',
                  cursor: 'pointer',
                  padding: '5px 2px',
                  boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
                }}
                onClick={() => setIsRightPanelCollapsed(!isRightPanelCollapsed)}
              >
                <IconButton size="small" sx={{ padding: '2px' }}>
                  {isRightPanelCollapsed ? (
                    <KeyboardArrowLeftIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowRightIcon fontSize="small" />
                  )}
                </IconButton>
              </div>

              {/* 패널 컨텐츠 */}
              <div style={{
                width: isRightPanelCollapsed ? 0 : '100%',
                height: '100%',
                borderLeft: isRightPanelCollapsed ? 'none' : '1px solid #ddd',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                whiteSpace: 'nowrap',
                opacity: isRightPanelCollapsed ? 0 : 1,
                transition: 'opacity 0.2s ease',
              }}>
                <TabContainer>
                  <TabButton
                    active={activeTab === 0}
                    onClick={() => setActiveTab(0)}
                  >
                    {translateText({text: '이미지 업로드', globalLanguage: language})}
                  </TabButton>
                  <TabButton
                    active={activeTab === 1}
                    onClick={() => setActiveTab(1)}
                  >
                    {translateText({text: '제품 설명서', globalLanguage: language})}
                  </TabButton>
                  <TabButton
                    active={activeTab === 2}
                    onClick={() => setActiveTab(2)}
                  >
                    {translateText({text: '변경 히스토리', globalLanguage: language})}
                  </TabButton>
                </TabContainer>
                <TabContent>
                  {activeTab === 0 && (
                    <ImgUploadTab data={selectedRow} changeImg={handleImageSrcUpdate}/>
                  )}
                  {activeTab === 1 && (
                    <PrdtDescriptionTab data={selectedRow} />
                  )}
                  {activeTab === 2 && (
                    <ChangeCommentTab data={selectedRow} />
                  )}
                </TabContent>
              </div>
            </div>
          </div>
        </Content>
        <div style={{ 
          width: '100%', 
          paddingLeft: '50px',
          margin: '-25px 0 5px 0',
          display: 'flex', 
          justifyContent: 'flex-start' ,
          height: '20px'
        }}>
          <span style={{ fontSize: '0.8rem' }}>
            {translateText({text: '스타일 수 :', globalLanguage: language})} {filteredTableData.length}
            &nbsp;&nbsp;
            {translateText({text: 'SKU 수 :', globalLanguage: language})} {filteredTableData.reduce((sum, row) => sum + (row.color_list ? row.color_list.split(',').filter(c => c.trim()).length : 0), 0)}
          </span>
        </div>
      </Dashboard>}
    </Page>
  );
}

export default CadDataDX;


const columns = (language) => [
  // 기본 정보
  { field: 'part_cd', headerName: '품번 ', width: 100 },
  {
    field: 'img',
    headerName: '이미지',
    width: 90,
    renderCell: (props) =>
      (
        <div style={{ position: 'relative', width: '2vw', height: '3.5vh' }}>
          <img
            src={props.value}
            onError={(e) => {
              e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              margin: '3px 0 3px 0',
            }}
          />
        </div>
      )
  },

  // 제품 정보
  language === 'ko' ? {
    field: 'prdt_nm', 
    headerName: '제품명', 
    width: 300 
  } : {
    field: 'prdt_nm_eng', 
    headerName: 'Description', 
    width: 300 
  },
  
  // 분류 정보
  { field: 'parent_prdt_kind_nm', headerName: '대분류', width: 90 },
  { field: 'prdt_kind_nm', headerName: '중분류', width: 100 },
  { field: 'item', headerName: '아이템', width: 90 },
  { field: 'fab_info', headerName: '소재명', width: 150 },
  
  // 시즌 정보

  { field: 'sex', headerName: '성별', width: 90 },
  
  
  
  // 스타일 정보
  { field: 'fit_info', headerName: '핏', width: 100 },
  { field: 'length_info', headerName: '기장', width: 100 },

  
  // 가격 정보
  { field: 'tag_price', headerName: '한국택가', width: 110 },
  { field: 'tag_price_cn', headerName: '중국택가', width: 110 },
  
  // 협력사 정보
  { field: 'mfac_compy_nm', headerName: '협력사', width: 200 },

  // 담당자 정보
  { field: 'dsgnr_nm', headerName: '디자이너', width: 100 },
  
  // 컬러 정보
  { field: 'color_list', headerName: '컬러', width: 250 },
  
  
];

 
  const style = {
    width: '100%',
    fontSize: '0.8rem',
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: '#F9F9FB !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 'bold',
    },
    '& .Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.03) !important',
    },
    '& .MuiCheckbox-root': {
      color: '#666666',
      '&.Mui-checked': {
        color: '#666666',
      }
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer'
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none'
    }
  };

 
  const createOptions = (data, key, filterFn = (value) => value !== null) => {
    const options = Array.from(
      new Set(
        data.map((item) => (item[key] === null ? '-' : item[key])).filter(filterFn)
      )
    ).map((value) => ({
      value: key === 'lifestyle_nm' ? (value && value.toUpperCase ? value.toUpperCase() : value) : value,
      label: key === 'lifestyle_nm' ? (value && value.toUpperCase ? value.toUpperCase() : value) : value
    }));

    // 중복 제거 (distinct)
    const distinctOptions = Array.from(
      new Map(options.map((item) => [item.value, item])).values()
    );

    return distinctOptions.sort((a, b) => {
      // sesn : 숫자 내림차순, 영문 오름차순 정렬
      if (key === 'sesn') {
        const numA = parseInt(a.value, 10);
        const numB = parseInt(b.value, 10);
        const alphaA = a.value.slice(-2); // 'FW', 'SS' 등
        const alphaB = b.value.slice(-2);
  
        if (numA !== numB) {
          return numB - numA; // 숫자 내림차순
        }
        return alphaA.localeCompare(alphaB); // 영문 오름차순
      } else {
        // 기본 정렬 (예: '-'가 가장 먼저, 나머지는 알파벳 순)
        if (a.value === '-') return -1;
        if (b.value === '-') return 1;
        return a.value.localeCompare(b.value);
      }
    });
  };


// Grid 
const Grid = ({ tableData, setSelectedRow, language, scrollContainerRef }) => {
  const itemsPerPage = 100; // 한 페이지에 100개씩 보여줄 것
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지 상태

  // tableData가 변경될 때마다 페이지를 1로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [tableData]);

  // 데이터의 시작 인덱스와 끝 인덱스를 계산하여 해당 페이지에 보여줄 데이터 추출
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);

  // 페이지 변경 시 호출되는 함수
  const handlePageChange = (event, value) => {
    setCurrentPage(value); // 페이지 변경 시 currentPage 상태 업데이트
    
    // 스크롤을 맨 위로 이동
    if (scrollContainerRef?.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  };

  const cardStyle = {
    border: "1px solid #ddd",
    borderRadius: "5px",
    padding: "10px",
    textAlign: "center",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
    cursor: "pointer",
    fontSize: "11px",
  };

  const imageStyle = {
    width: "100%",
    height: "100px",
    objectFit: 'contain',
    borderRadius: "4px",
  };

  return (
    <div>
      { tableData.length === 0 ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '0.9rem',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0
        }}>
          No data
        </div>
      ) : (
        <div>
        <div style={gridStyle}>
      {currentItems.map((item, index) => (
          <div
            key={index}
            style={cardStyle}
            onClick={() => setSelectedRow(item)} // 클릭된 데이터를 부모로 전달
          >
            <FlexBoxVertical>
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div style={{fontWeight: 'bold'}}>{item.part_cd !== null ? item.part_cd : ' '}</div>
                {language === 'ko' ? (
                  <div style={{fontWeight: 'bold', color: 'rgb(119, 119, 119)'}}>{item.prdt_nm !== null ? item.prdt_nm : ' '}</div>
                ) : (
                  <div style={{fontWeight: 'bold', color: 'rgb(119, 119, 119)'}}>{item.prdt_nm_eng !== null ? item.prdt_nm_eng : ' '}</div>
                )}
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "center" }}>
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div>{item.size_range !== null ? item.size_range : ' '}</div>
                <div>{item.fit_info !== null ? item.fit_info : ' '}</div>
                <div>{item.tag_price != null ? item.tag_price.toLocaleString() : ' '}</div>
                <div>{item.tag_price_cn != null ? item.tag_price_cn.toLocaleString() : ' '}</div>
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div>{item.dsgnr_nm !== null ? item.dsgnr_nm : ' '}</div>
                <div>{item.dsgnr_md_nm !== null ? item.dsgnr_md_nm : ' '}</div>
                <div>{item.dsgnr_goods_nm !== null ? item.dsgnr_goods_nm : ' '}</div>
                <div>{item.dsgnr_mtrls_nm !== null ? item.dsgnr_mtrls_nm : ' '}</div>
              </FlexBoxHorizontal>
              <img
                src={item.img !== null ? item.img : ' '} // 이미지 URL
                alt={`Style ${item.part_cd}`}
                onError={(e) => {
                  e.target.src =
                    "https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0";
                }}
                style={imageStyle}
              />
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div>{item.mfac_compy_nm !== null ? item.mfac_compy_nm : ' '}</div>
                <div>{item.fab_info !== null ? item.fab_info : ' '}</div>
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "center" }}>
                <div style={{ 
                  textAlign: 'center',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  lineHeight: '1.2',
                  width: '100%'
                }}>
                  {item.color_list !== null ? item.color_list : ' '}
                </div>
              </FlexBoxHorizontal>
            </FlexBoxVertical>
          </div>
        ))}
      </div>
      {/* 페이지네이션 추가 */}
      <Stack spacing={2} style={{ alignItems: 'center', margin: '0 0 20px 0' }}>
        <Pagination
          count={Math.ceil(tableData.length / itemsPerPage)} // 전체 페이지 수 계산
          page={currentPage} // 현재 페이지
          onChange={handlePageChange} // 페이지 변경 시 실행되는 함수
        />
      </Stack>
    </div>
      )}
      </div>
      
  );
};

// 탭 스타일드 컴포넌트들
const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #ddd;
  background-color: #f8f9fa;
  flex-shrink: 0;
`;

const TabButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background-color: ${props => props.active ? '#fff' : 'transparent'};
  color: ${props => props.active ? palette.button.primary : '#666'};
  font-size: 14px;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  cursor: pointer;
  border-bottom: ${props => props.active ? `2px solid ${palette.button.primary}` : '2px solid transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#fff' : '#f0f0f0'};
    color: ${props => props.active ? palette.button.primary : '#333'};
  }
  
  &:not(:last-child) {
    border-right: 1px solid #eee;
  }
`;

const TabContent = styled.div`
  flex: 1;
  overflow: auto;
  background-color: #fff;
  min-height: 0; /* flex item이 줄어들 수 있도록 설정 */
`;